import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useOrganizationContext } from "@/orgs/useOrganizationContext";
import { useQuery } from "@tanstack/react-query";
import log from "loglevel";
import { ReactNode, useCallback, useEffect, useState } from "react";
import rawUseWebSocket from "react-use-websocket";
import { isError } from "remeda";
import { useAuthentication } from "../auth";
import {
  cloudRenderingSessionManagementServiceUrl,
  desktopClientOAuth2ClientId,
  portalBackendUrl,
} from "../config";
import {
  availableLocally,
  connectionAttemptFailed,
  connectionLost,
  handshakeCompleted,
  selectDesktopClient,
} from "../features/desktopClientSlice";
import { verifyDeviceAuthorizationRequest } from "../portal-api";
import { DesktopClientContext } from "./context";
import {
  type MessageCapability,
  Message,
  type ServerHelloMessage,
  type ServiceMessage,
  type VerifyAuthorizationMessage,
  acknowledgeMessage,
  buildMessageHeader,
  helloMessage,
} from "./messages";

// `react-use-websocket` is published as CommonJS and exposes the hook only as
// its `default` export. Because this package is `"type": "module"`, the default
// import from a CJS module is ambiguous under Vite's bundler (rolldown): it may
// resolve either to the hook itself or to the whole `module.exports` namespace
// that carries it under `.default`. That mismatch intermittently crashed the app
// at render with "TypeError: (0 , X.default) is not a function" (PORTAL-FRONTEND-7C).
// Normalize once at module load, per rolldown's recommended interop check:
// https://rolldown.rs/in-depth/bundling-cjs#ambiguous-default-import-from-cjs-modules
type UseWebSocket = typeof rawUseWebSocket;
const importedUseWebSocket = rawUseWebSocket as
  | UseWebSocket
  | { __esModule?: boolean; default: UseWebSocket };
const useWebSocket = (
  typeof importedUseWebSocket === "object" &&
  importedUseWebSocket !== null &&
  importedUseWebSocket.__esModule
    ? importedUseWebSocket.default
    : importedUseWebSocket
) as UseWebSocket;

const portalBackendHost = new URL(portalBackendUrl).host;

const desktopClientWebsocketHost = "127.0.0.1";
const desktopClientWebsocketPort = 35987;
const defaultLocalConnectionPollingInterval = 5000;
const localWebsocketConnectionTimeout = 500;

/** Convert a message capabilities array to a name → version map */
function toMessageVersionMap(
  capabilities: MessageCapability[],
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const { Name, Version } of capabilities) {
    // if the DC reports multiple versions for the same message, use the highest
    if (!(Name in map) || Version > map[Name]) {
      map[Name] = Version;
    }
  }
  return map;
}

export function DesktopClientProvider({ children }: { children?: ReactNode }) {
  const dispatch = useAppDispatch();
  const auth = useAuthentication();
  const { isConnected, isAvailableLocally, supportedIncomingMessages } =
    useAppSelector(selectDesktopClient);
  const organization = useOrganizationContext();
  const [localConnectionPollingInterval, setLocalConnectionPollingInterval] =
    useState(defaultLocalConnectionPollingInterval);

  // query to poll connection to local desktop client.
  // dispatch and setLocalConnectionPollingInterval are stable; pass them through
  // the query's `meta` so the queryFn reads them from its context instead of
  // closing over them (which would trip @tanstack/query/exhaustive-deps).
  useQuery({
    queryKey: ["desktop-client-service"],
    meta: { dispatch, setLocalConnectionPollingInterval },
    queryFn: async ({ signal, meta }) => {
      const { dispatch, setLocalConnectionPollingInterval } = meta as {
        dispatch: ReturnType<typeof useAppDispatch>;
        setLocalConnectionPollingInterval: (interval: number) => void;
      };
      try {
        const res = await new Promise<void>((resolve, reject) => {
          let websocketTest: WebSocket | null = null;

          const cleanupWebsocket = () => {
            if (websocketTest) {
              websocketTest.onopen = null;
              websocketTest.onerror = null;
              websocketTest.close();
            }
          };

          // if the socket doesn't connect in 300ms, DC isn't running locally
          setTimeout(() => {
            // timeout
            cleanupWebsocket();
            reject(new Error("timeout"));
          }, localWebsocketConnectionTimeout);

          signal?.addEventListener("abort", () => {
            cleanupWebsocket();
            reject(new Error("abort"));
          });

          // opening up websocket connection
          websocketTest = new WebSocket(
            `ws://${desktopClientWebsocketHost}:${desktopClientWebsocketPort}/service`,
          );
          // on error or success -> resolve
          websocketTest.onopen = () => {
            resolve();
            cleanupWebsocket();
          };
          websocketTest.onerror = () => {
            reject(new Error("error"));
          };
        });

        // reset polling interval as soon as we succeeded
        setLocalConnectionPollingInterval(
          defaultLocalConnectionPollingInterval,
        );
        dispatch(availableLocally());

        return res;
      } catch (error) {
        // in any case, keep track of the failed connection attempt
        dispatch(connectionAttemptFailed());

        // we need to catch errors and turn them into a successful response so that refetchinterval works
        // this is a workaround for retrying the query, see https://github.com/TanStack/query/issues/7295
        if (isError(error)) {
          if (error.message === "timeout") {
            return "timeout";
          } else if (error.message === "abort") {
            return "abort";
          } else if (error.message === "error") {
            return "error";
          }
        }

        throw error;
      }
    },
    refetchInterval: isAvailableLocally
      ? false
      : () => localConnectionPollingInterval,
  });

  // establish an unscoped (no user context) connection to see whether the desktop client is online at all
  const { sendJsonMessage, lastJsonMessage } = useWebSocket<ServiceMessage>(
    `ws://${desktopClientWebsocketHost}:${desktopClientWebsocketPort}/service`,
    {
      shouldReconnect: () => true,
      onOpen: () => {
        if (!isConnected) {
          sendJsonMessage(helloMessage);
        }
      },
      onClose: () => {
        if (isConnected) {
          dispatch(connectionLost());
        }
      },
      reconnectAttempts: Number.MAX_VALUE,
      // just one connection!
      share: true,
    },
    !!auth.user && isAvailableLocally,
  );

  const verifyAuthorization = useCallback(
    async (userCode: string) => {
      try {
        await verifyDeviceAuthorizationRequest(userCode, organization);
      } catch (error) {
        log.error("Error during device auth for desktop client: ", error);
      }
    },
    [organization],
  );

  useEffect(() => {
    if (!lastJsonMessage) return;

    const lastMessageHeader = lastJsonMessage.Header;
    switch (lastMessageHeader?.Name) {
      case Message.SERVER_HELLO: {
        sendJsonMessage(acknowledgeMessage);
        const serverHello = lastJsonMessage as ServerHelloMessage;
        dispatch(
          handshakeCompleted({
            name: serverHello.ServerName,
            supportedIncomingMessages: toMessageVersionMap(
              serverHello.IncomingMessages,
            ),
            supportedOutgoingMessages: toMessageVersionMap(
              serverHello.OutgoingMessages,
            ),
          }),
        );
        break;
      }
      case Message.VERIFY_DEVICE_AUTHORIZATION_REQUEST:
        if (lastJsonMessage.BackendUrl === portalBackendHost) {
          verifyAuthorization(
            (lastJsonMessage as VerifyAuthorizationMessage).UserCode,
          );
        }
        break;
      default:
        log.debug(`Received unsupported message`, lastJsonMessage);
        break;
    }
  }, [lastJsonMessage, sendJsonMessage, dispatch, verifyAuthorization]);

  const requestAuthentication = useCallback(() => {
    const version = supportedIncomingMessages[Message.REQUEST_AUTHENTICATION];
    if (!version) return;
    sendJsonMessage({
      Header: buildMessageHeader(Message.REQUEST_AUTHENTICATION, version),
      Name: portalBackendHost,
      BackendUrl: portalBackendUrl,
      ClientId: desktopClientOAuth2ClientId,
      SessionManagementUrl: `${cloudRenderingSessionManagementServiceUrl}/hub/sessions`,
    });
  }, [sendJsonMessage, supportedIncomingMessages]);

  return (
    <DesktopClientContext
      value={{
        requestAuthentication,
        setLocalConnectionPollingInterval: (newInterval: number) => {
          if (newInterval < 0) {
            throw new Error("Polling interval must be positive");
          }

          if (newInterval < localWebsocketConnectionTimeout + 100) {
            throw new Error(
              `Minimium polling interval is ${localWebsocketConnectionTimeout + 100}ms`,
            );
          }

          setLocalConnectionPollingInterval(newInterval);
        },
        resetLocalConnectionPollingInterval: useCallback(
          () =>
            setLocalConnectionPollingInterval(
              defaultLocalConnectionPollingInterval,
            ),
          [],
        ),
      }}
    >
      {children}
    </DesktopClientContext>
  );
}
